"use client";

import { usePathname } from 'next/navigation';
import { useCompetitionContext } from "./providers/CompetitionContext";
import { useState, useEffect } from 'react';

import { SquareX } from 'lucide-react'
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { Bars3Icon, BellIcon, XMarkIcon } from '@heroicons/react/24/outline'

export default function NavMenu() { 
  const { brand } = useCompetitionContext();
  const pathname = usePathname();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Read teamId from cookie on client side
  useEffect(() => {
    const getCookieValue = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        const popped = parts.pop();
        return popped ? popped.split(';').shift() || null : null;
      }
      return null;
    };

    // Check for authentication WONT WORK ON DPG COOKIES
    setIsAuthenticated(!!getCookieValue('userInfo'));

    // Get teamId from cookie
    const teamIdFromCookie = getCookieValue('teamId');
    if (teamIdFromCookie && teamIdFromCookie !== '') {
      setTeamId(teamIdFromCookie);
    }
    console.log('nav teamIdFromCookie', teamIdFromCookie);

  }, []);

  // Function to delete a cookie by setting its expiration date to the past
  const deleteCookie = (name: string, path = '/', domain = '') => {
    // For regular cookies
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    
    // For secure cookies, we need to set the secure flag
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; secure`;
    
    // If domain is provided, also try to delete with domain specified
    if (domain) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; secure`;
    }
  };

  // Main signOut function to remove all specified cookies and redirect
  const signOut = ({ callbackUrl = "/" } = {}) => {
    // List of cookies to delete
    const cookiesToDelete = [
      'user',
      'dpg-component-at',
      'dpg-component-uit',
      'teamId',
      'userInfo'
    ];
    
    // Delete each cookie
    cookiesToDelete.forEach(cookieName => {
      deleteCookie(cookieName);
      
      // Try different paths in case cookies were set with specific paths
      deleteCookie(cookieName, '/fantasy');
      deleteCookie(cookieName, '/api');
      
      // Also try with domain and subdomain variations if needed
      const domain = window.location.hostname;
      if (domain) {
        deleteCookie(cookieName, '/', domain);
        
        // If on a subdomain, try removing from root domain too
        if (domain.split('.').length > 2) {
          const rootDomain = domain.split('.').slice(-2).join('.');
          deleteCookie(cookieName, '/', rootDomain);
        }
      }
    });
    
    // Reset authentication state
    setIsAuthenticated(false);
    setTeamId(null);
    
    // Redirect to the callback URL
    //todo https://account.ad.nl/logout?callbackUrl=https://goudenspelen.ad.nl/?state=1234)
    window.location.href = callbackUrl;
  };

  // Base navigation items
  let navigation = [
    { name: 'Klassement', href: '/fantasy/rankings', current: false },
    // { name: 'Stats', href: '/stats', current: false },
    { name: 'Prijzen', href: '/prizes', current: false },
    { name: 'Spelregels', href: '/rules', current: false },
  ].map(item => ({
    ...item,
    current: pathname === item.href
  }));

  // Dynamic navigation based on authentication and team status
  // if (teamId) {
    const fantasyUrl = teamId ? `/fantasy/edit/${teamId}` : '/fantasy/new';
    
    // Insert Game menu item at the beginning
    navigation = [
      { 
        name: 'Spel', 
        href: fantasyUrl, 
        current: pathname === fantasyUrl || pathname.startsWith(`/fantasy/edit/`) 
      },
      ...navigation
    ];
    
    // Insert Leagues item if user has a team
    if (teamId) {
      navigation.splice(1, 0, { 
        name: 'Leagues', 
        href: '/fantasy/leagues', 
        current: pathname === '/fantasy/leagues' 
      });
    }
  // }
  
  function classNames(...classes: any) {
    return classes.filter(Boolean).join(' ')
  }

  return (
    <Disclosure as="nav" className={`bg-${brand}-header mt-4 rounded-t-lg`}>
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <div className="flex items-center">
                <div className="flex flex-row items-center gap-0 shrink-0">
                  {(brand !== 'nu') && <a href={`https://${brand}.nl`} className="block">
                    <img 
                      src={`https://dpgm.b-cdn.net/files/${brand}.svg`} 
                      className="h-12 w-auto mr-4"
                      alt={`${brand} Logo`}
                    />
                  </a>}
                  <a href="/" className="block">
                    <img
                      src={`https://dpgm.b-cdn.net/files/${(brand === 'nu') ? `eks-logo` : `eks-adr-logo`}.svg`}
                      className="h-10 w-auto"
                      alt="Logo EK Spel"
                    />
                  </a>
                </div>
                <div className="hidden md:block">
                  <div className="ml-10 flex items-baseline space-x-4">
                    {navigation.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        aria-current={item.current ? 'page' : undefined}
                        className={classNames(
                          item.current
                            ? `bg-white text-${brand}-header`
                            : `text-white hover:bg-white hover:text-${brand}-contrast`,
                          `rounded-md px-3 py-2 text-sm font-medium`
                        )}
                      >
                        {item.name}
                      </a>
                    ))}
                    
                  </div>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="ml-4 flex items-center md:ml-6">
                  {(brand !== 'nu') && isAuthenticated && (
                    <button 
                      className="text-white hover:text-gray-200 px-3 py-2 text-sm font-medium" 
                      onClick={() => signOut({ callbackUrl: "/" })}
                    >
                      Uitloggen
                    </button>
                  )}
                  {/* Profile dropdown and other buttons would go here */}
                </div>
              </div>
              <div className="-mr-2 flex md:hidden">
                {/* Mobile menu button */}
                <DisclosureButton className={`group relative inline-flex items-center justify-center rounded-md bg-${brand}-header text-white`}>
                  <span className="absolute -inset-0.5" />
                  <span className="pr-2">Menu</span>
                  <Bars3Icon aria-hidden="true" className="block size-6 group-data-[open]:hidden" />
                  <SquareX fill='#FFF' color='#dc0714' aria-hidden="true" className="hidden size-6 group-data-[open]:block" />
                </DisclosureButton>
              </div>
            </div>
          </div>
          <DisclosurePanel className="md:hidden">
            <div className="bg-gpspel-grey-light space-y-1 px-2 pb-3 pt-2 sm:px-3">
              {navigation.map((item) => (
                <DisclosureButton
                  key={item.name}
                  as="a"
                  href={item.href}
                  aria-current={item.current ? 'page' : undefined}
                  className={classNames(
                    item.current ? 'text-default font-extra underline underline-offset-4' : 'hover:font-extra hover:underline hover:underline-offset-4',
                    'block rounded-md px-3 py-2 text-base font-medium',
                  )}
                >
                  {item.name}
                </DisclosureButton>
              ))}
            </div>
            <div className="bg-gpspel-grey-light pb-3">
              <div className="flex items-center px-5">
                {/* User profile section would go here */}
              </div>
              <div className="mt-3 space-y-1 px-5">
                {(brand !== 'nu') && isAuthenticated && (
                  <button 
                    className="block px-3 py-2 text-base font-medium hover:font-extra hover:underline hover:underline-offset-4" 
                    onClick={() => signOut({ callbackUrl: "/" })}
                  >
                    Uitloggen
                  </button>
                )}
                {/* User navigation section would go here */}
              </div>
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
}